import { supabase } from '../lib/supabase';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

// Exact 7 status codes matching n8n
const CONTAINER_STATUS_CODES = {
  AU_PORT_LE_HAVRE_ANVERS: 'AU_PORT_LE_HAVRE_ANVERS',
  EN_TRANSIT_MARITIME: 'EN_TRANSIT_MARITIME',
  ARRIVE_A_ABIDJAN: 'ARRIVE_A_ABIDJAN',
  DEDOUANEMENT_EN_COURS: 'DEDOUANEMENT_EN_COURS',
  TRANSFERT_A_ENTREPOT: 'TRANSFERT_A_ENTREPOT',
  DISTRIBUTION_EN_COURS: 'DISTRIBUTION_EN_COURS',
  CONTENEUR_TERMINE: 'CONTENEUR_TERMINE'
};

// French labels for display only - NEVER send these to DB
const STATUS_DISPLAY_LABELS = {
  'AU_PORT_LE_HAVRE_ANVERS': 'Au port Le Havre / Anvers',
  'EN_TRANSIT_MARITIME': 'En mer',
  'ARRIVE_A_ABIDJAN': 'Arrivé au port d\'Abidjan',
  'DEDOUANEMENT_EN_COURS': 'En phase de dédouanement',
  'TRANSFERT_A_ENTREPOT': 'À l\'entrepôt / magasin',
  'DISTRIBUTION_EN_COURS': 'Distribution en cours',
  'CONTENEUR_TERMINE': 'Conteneur terminé'
};

// Reverse mapping: French label -> Status code (for backward compatibility)
const LABEL_TO_CODE = {
  'Au port Le Havre / Anvers': 'AU_PORT_LE_HAVRE_ANVERS',
  'En mer': 'EN_TRANSIT_MARITIME',
  'Arrivé au port d\'Abidjan': 'ARRIVE_A_ABIDJAN',
  'En phase de dédouanement': 'DEDOUANEMENT_EN_COURS',
  'À l\'entrepôt / magasin': 'TRANSFERT_A_ENTREPOT',
  'Distribution en cours': 'DISTRIBUTION_EN_COURS',
  'Conteneur terminé': 'CONTENEUR_TERMINE',
  // Old status mappings for backward compatibility
  'Brouillon': 'AU_PORT_LE_HAVRE_ANVERS',
  'Ouvert': 'AU_PORT_LE_HAVRE_ANVERS',
  'En transit': 'EN_TRANSIT_MARITIME',
  'Au port': 'ARRIVE_A_ABIDJAN',
  'Dédouanement': 'DEDOUANEMENT_EN_COURS',
  'À l\'entrepôt': 'TRANSFERT_A_ENTREPOT',
  'Distribution': 'DISTRIBUTION_EN_COURS',
  'Fermé': 'CONTENEUR_TERMINE',
  'Terminé': 'CONTENEUR_TERMINE',
  // Old lowercase DB enum values (backward compatibility)
  'draft': 'AU_PORT_LE_HAVRE_ANVERS',
  'open': 'AU_PORT_LE_HAVRE_ANVERS',
  'closed': 'AU_PORT_LE_HAVRE_ANVERS',
  'departed': 'EN_TRANSIT_MARITIME',
  'arrived': 'ARRIVE_A_ABIDJAN',
  'port_le_havre_antwerp': 'AU_PORT_LE_HAVRE_ANVERS',
  'at_sea': 'EN_TRANSIT_MARITIME',
  'arrived_abidjan': 'ARRIVE_A_ABIDJAN',
  'customs_clearance': 'DEDOUANEMENT_EN_COURS',
  'warehouse_abidjan': 'TRANSFERT_A_ENTREPOT',
  'en_distribution': 'DISTRIBUTION_EN_COURS',
  'termine': 'CONTENEUR_TERMINE'
};

/**
 * Get French display label for a status code
 * @param {string} statusCode - The status code from DB
 * @returns {string} French label for display
 */
export const getStatusLabel = (statusCode) => {
  return STATUS_DISPLAY_LABELS?.[statusCode] || 'Statut inconnu';
};

/**
 * Convert French label to status code (for backward compatibility)
 * @param {string} label - French label
 * @returns {string} Status code for DB
 */
export const labelToCode = (label) => {
  return LABEL_TO_CODE?.[label] || label;
};

/**
 * Get all valid status codes
 * @returns {Array} Array of status code objects
 */
export const getValidStatusCodes = () => {
  return Object.entries(STATUS_DISPLAY_LABELS)?.map(([code, label]) => ({
    code,
    label
  }));
};

/**
 * Validate if a status code is valid
 * @param {string} statusCode - Status code to validate
 * @returns {boolean} True if valid
 */
export const isValidStatusCode = (statusCode) => {
  return Object.values(CONTAINER_STATUS_CODES)?.includes(statusCode);
};

/**
 * Container Service - Handles all container-related operations
 */
export const containerService = {
  /**
   * Get all containers with optional filters
   */
  async getAll(filters = {}) {
    try {
      let query = supabase?.from('containers')?.select('*')?.order('created_at', { ascending: false });

      // Filter by archived status
      if (filters?.showArchived !== undefined) {
        query = query?.eq('is_archived', filters?.showArchived);
      } else {
        // By default, show only non-archived containers
        query = query?.eq('is_archived', false);
      }

      // Apply other filters if provided
      if (filters?.status) {
        // Convert label to code if needed
        const statusCode = labelToCode(filters?.status);
        query = query?.eq('status', statusCode);
      }
      if (filters?.searchTerm) {
        query = query?.or(`container_code.ilike.%${filters?.searchTerm}%,departure_port.ilike.%${filters?.searchTerm}%,destination_port.ilike.%${filters?.searchTerm}%`);
      }

      const { data, error } = await query;

      if (error) throw error;

      // ✅ FIX: Map snake_case DB columns to camelCase for React
      return data?.map(container => ({
        id: container?.id,
        containerCode: container?.container_code,
        departurePort: container?.departure_port,
        destinationPort: container?.destination_port,
        departureDate: container?.departure_date,
        etaDate: container?.eta_date,
        shippingLine: container?.shipping_line,
        vesselName: container?.vessel_name,
        status: container?.status,
        notes: container?.notes,
        totalShipments: container?.total_shipments,
        totalPackages: container?.total_packages,
        createdAt: container?.created_at,
        updatedAt: container?.updated_at,
        isArchived: container?.is_archived || false,
        statusLabel: getStatusLabel(container?.status)
      })) || [];
    } catch (error) {
      console.error('Error fetching containers:', error);
      throw error;
    }
  },

  /**
   * Get containers with status 'draft' or 'open' for shipment selection
   * Includes all statuses where a container is still open for assignment:
   * - AU_PORT_LE_HAVRE_ANVERS (en préparation / ouvert)
   * - EN_TRANSIT_MARITIME (en mer, peut encore recevoir des affectations)
   * Excludes: archived, CONTENEUR_TERMINE, ARRIVE_A_ABIDJAN, DEDOUANEMENT_EN_COURS, TRANSFERT_A_ENTREPOT, DISTRIBUTION_EN_COURS
   */
  async getOpenContainers() {
    try {
      const today = new Date()?.toISOString()?.split('T')?.[0]; // 'YYYY-MM-DD'

      const { data, error } = await supabase
        ?.from('containers')
        ?.select('*')
        ?.eq('is_archived', false)
        ?.gte('departure_date', today)
        ?.not(
          'status',
          'in',
          '(ARRIVE_A_ABIDJAN,DEDOUANEMENT_EN_COURS,TRANSFERT_A_ENTREPOT,DISTRIBUTION_EN_COURS,CONTENEUR_TERMINE)'
        )
        ?.order('departure_date', { ascending: true });

      if (error) throw error;

      // Warn about EN_TRANSIT_MARITIME containers with future departure dates (data inconsistency)
      (data || [])?.forEach(container => {
        if (
          container?.status === 'EN_TRANSIT_MARITIME' &&
          container?.departure_date &&
          container?.departure_date >= today
        ) {
          console.warn(
            `⚠️ Incohérence détectée : le conteneur ${container?.container_code} (id: ${container?.id}) ` +
            `a le statut EN_TRANSIT_MARITIME mais sa date de départ (${container?.departure_date}) est future ou aujourd'hui. ` +
            `Ce conteneur est affiché dans le sélecteur car sa date de départ est valide, ` +
            `mais son statut devrait être corrigé en base (ex: AU_PORT_LE_HAVRE_ANVERS).`
          );
        }
      });

      return (data || [])?.map(container => ({
        id: container?.id,
        containerCode: container?.container_code,
        departurePort: container?.departure_port,
        destinationPort: container?.destination_port,
        departureDate: container?.departure_date,
        etaDate: container?.eta_date,
        status: container?.status,
        statusLabel: getStatusLabel(container?.status)
      })) || [];
    } catch (error) {
      console.error('Error fetching open containers:', error);
      throw error;
    }
  },

  /**
   * Get single container by ID with calculated total packages
   */
  async getById(id) {
    try {
      const { data, error } = await supabase?.from('containers')?.select(`*,shipments (id,reference,client_id,current_status,created_at,clients (id,name,email,phone))`)?.eq('id', id)?.single();

      if (error) throw error;

      // ✅ FIX: Properly map ALL snake_case DB columns to camelCase for React component
      return {
        id: data?.id,
        containerCode: data?.container_code,
        departurePort: data?.departure_port,
        destinationPort: data?.destination_port,
        departureDate: data?.departure_date,
        etaDate: data?.eta_date,
        shippingLine: data?.shipping_line,
        vesselName: data?.vessel_name,
        status: data?.status,
        notes: data?.notes,
        totalShipments: data?.total_shipments,
        totalPackages: data?.total_packages,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at,
        shipments: data?.shipments,
        statusLabel: getStatusLabel(data?.status)
      };
    } catch (error) {
      console.error('Error fetching container:', error);
      throw error;
    }
  },

  /**
   * Get single container by container code (e.g. ADT-0426-CIV)
   */
  async getByCode(code) {
    try {
      const { data, error } = await supabase
        ?.from('containers')
        ?.select('*')
        ?.ilike('container_code', code?.trim())
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        containerCode: data?.container_code,
        departurePort: data?.departure_port,
        destinationPort: data?.destination_port,
        departureDate: data?.departure_date,
        etaDate: data?.eta_date,
        shippingLine: data?.shipping_line,
        vesselName: data?.vessel_name,
        status: data?.status,
        notes: data?.notes,
        totalShipments: data?.total_shipments,
        totalPackages: data?.total_packages,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at,
        isArchived: data?.is_archived || false,
        statusLabel: getStatusLabel(data?.status)
      };
    } catch (error) {
      console.error('Error fetching container by code:', error);
      throw error;
    }
  },

  /**
   * Get shipments for a specific container with payment status
   */
  async getContainerShipments(containerId) {
    try {
      // Query shipments table directly with all needed columns including payment_status
      const { data, error } = await supabase
        ?.from('shipments')
        ?.select('id, reference, sender_name, sender_phone, sender_address, sender_city, sender_country, receiver_name, receiver_phone, receiver_address, receiver_city, receiver_country, current_status, payment_status, paid_amount, discount_amount, created_at')
        ?.eq('container_id', containerId)
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      // Get total_amount AND total packages (quantity) for each shipment by summing shipment_items
      const shipmentsWithTotals = await Promise.all(
        data?.map(async (shipment) => {
          const { data: itemsData } = await supabase
            ?.from('shipment_items')
            ?.select('price_total, quantity')
            ?.eq('shipment_id', shipment?.id);

          const totalAmount = (itemsData || [])?.reduce((sum, item) => sum + parseFloat(item?.price_total || 0), 0);
          // ✅ SOURCE DE VÉRITÉ : SUM(shipment_items.quantity)
          const totalPackages = (itemsData || [])?.reduce(
            (sum, item) => sum + (Number(item?.quantity) || 0),
            0
          );
          const discountAmount = parseFloat(shipment?.discount_amount || 0);
          const amountDue = totalAmount - discountAmount;

          console.log(`CONTAINER SHIPMENT — ${shipment?.sender_name} → totalPackages: ${totalPackages}`, {
            shipmentId: shipment?.id,
            reference: shipment?.reference,
            itemsData,
            totalPackages
          });

          return {
            id: shipment?.id,
            reference: shipment?.reference,
            senderName: shipment?.sender_name,
            senderPhone: shipment?.sender_phone,
            senderAddress: shipment?.sender_address,
            senderCity: shipment?.sender_city,
            senderCountry: shipment?.sender_country,
            receiverName: shipment?.receiver_name,
            receiverPhone: shipment?.receiver_phone,
            receiverAddress: shipment?.receiver_address,
            receiverCity: shipment?.receiver_city,
            receiverCountry: shipment?.receiver_country,
            currentStatus: shipment?.current_status,
            paymentStatus: shipment?.payment_status,
            paidAmount: parseFloat(shipment?.paid_amount || 0),
            createdAt: shipment?.created_at,
            totalAmount: totalAmount,
            // ✅ totalPackages = SUM(shipment_items.quantity) — source de vérité
            totalPackages: totalPackages,
            discountAmount: discountAmount,
            amountDue: amountDue
          };
        })
      );

      console.log('CONTAINER SHIPMENTS', shipmentsWithTotals);

      return shipmentsWithTotals;
    } catch (error) {
      console.error('Error fetching container shipments:', error);
      throw error;
    }
  },

  /**
   * Create new container
   */
  async create(containerData) {
    try {
      // Ensure status is a valid code
      const status = containerData?.status 
        ? labelToCode(containerData?.status)
        : CONTAINER_STATUS_CODES?.AU_PORT_LE_HAVRE_ANVERS;

      if (!isValidStatusCode(status)) {
        throw new Error(`Invalid status code: ${status}`);
      }

      // Map camelCase fields to snake_case DB columns
      const insertData = {
        container_code: containerData?.containerCode,
        departure_port: containerData?.departurePort,
        destination_port: containerData?.destinationPort,
        departure_date: containerData?.departureDate || null,
        eta_date: containerData?.etaDate || null,
        shipping_line: containerData?.shippingLine || null,
        vessel_name: containerData?.vesselName || null,
        notes: containerData?.notes || null,
        status
      };

      const { data, error } = await supabase?.from('containers')?.insert([insertData])?.select()?.single();

      if (error) throw error;

      // Add display label to response
      return {
        ...data,
        statusLabel: getStatusLabel(data?.status)
      };
    } catch (error) {
      console.error('Error creating container:', error);
      throw error;
    }
  },

  /**
   * Update container
   */
  async update(id, updates) {
    try {
      const updateData = {};
      
      if (updates?.containerCode !== undefined) updateData.container_code = updates?.containerCode;
      if (updates?.departurePort !== undefined) updateData.departure_port = updates?.departurePort;
      if (updates?.destinationPort !== undefined) updateData.destination_port = updates?.destinationPort;
      if (updates?.departureDate !== undefined) updateData.departure_date = updates?.departureDate;
      if (updates?.etaDate !== undefined) updateData.eta_date = updates?.etaDate;
      if (updates?.shippingLine !== undefined) updateData.shipping_line = updates?.shippingLine;
      if (updates?.vesselName !== undefined) updateData.vessel_name = updates?.vesselName;
      if (updates?.status !== undefined) updateData.status = updates?.status;
      if (updates?.notes !== undefined) updateData.notes = updates?.notes;

      const { data, error } = await supabase?.from('containers')?.update(updateData)?.eq('id', id)?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        containerCode: data?.container_code,
        status: data?.status
      };
    } catch (error) {
      console.error('Error updating container:', error);
      throw error;
    }
  },

  /**
   * Update container status
   */
  async updateStatus(containerId, newStatus) {
    try {
      // Ensure we're sending the status code, not the label
      const statusCode = labelToCode(newStatus);
      
      // Validate status code
      if (!isValidStatusCode(statusCode)) {
        throw new Error(`Invalid status code: ${statusCode}. Valid codes: ${Object.values(CONTAINER_STATUS_CODES).join(', ')}`);
      }

      const { data, error } = await supabase?.from('containers')?.update({ 
          status: statusCode,
          updated_at: new Date()?.toISOString()
        })?.eq('id', containerId)?.select()?.single();

      if (error) throw error;

      // Sync status to all linked shipments and add tracking history
      try {
        const { data: syncResult, error: syncError } = await supabase?.rpc(
          'sync_container_status_to_shipments',
          {
            p_container_id: containerId,
            p_new_container_status: statusCode
          }
        );

        if (syncError) {
          console.warn('[Container] Failed to sync status to shipments:', syncError);
        } else {
          console.log(`[Container] Synced status to ${syncResult?.updated_count || 0} shipments:`, syncResult?.message);
        }
      } catch (syncErr) {
        // Don't block the container update if sync fails
        console.warn('[Container] Sync to shipments failed (non-blocking):', syncErr);
      }

      // Add display label to response
      return {
        ...data,
        statusLabel: getStatusLabel(data?.status)
      };
    } catch (error) {
      console.error('Error updating container status:', error);
      throw error;
    }
  },

  /**
   * Delete container (only if no shipments)
   */
  async delete(id) {
    try {
      // Check if container has shipments
      const { data: shipments } = await supabase?.from('shipments')?.select('id')?.eq('container_id', id)?.limit(1);

      if (shipments && shipments?.length > 0) {
        throw new Error('Cannot delete container with existing shipments');
      }

      const { error } = await supabase?.from('containers')?.delete()?.eq('id', id);

      if (error) throw error;

      return { success: true };
    } catch (error) {
      console.error('Error deleting container:', error);
      throw error;
    }
  },

  /**
   * Delete a container permanently
   */
  async delete(id) {
    try {
      const { error } = await supabase
        ?.from('containers')
        ?.delete()
        ?.eq('id', id);

      if (error) throw error;

      return { success: true };
    } catch (error) {
      console.error('Error deleting container:', error);
      throw error;
    }
  },

  /**
   * Generate container manifest PDF
   */
  async generateManifestData(containerId) {
    try {
      // Get container details
      const container = await this.getById(containerId);
      
      // Get all shipments in container
      const shipments = await this.getContainerShipments(containerId);

      // Get shipment items with detailed descriptions for each shipment
      const shipmentsWithPackages = await Promise.all(
        shipments?.map(async (shipment) => {
          // ✅ SOURCE DE VÉRITÉ : quantity depuis shipment_items
          const { data: itemsData } = await supabase
            ?.from('shipment_items')
            ?.select(`
              quantity,
              catalog_item_id,
              catalog_items (
                name,
                description
              )
            `)
            ?.eq('shipment_id', shipment?.id);

          // ✅ OBJECTIF 1 & 4 : Nb colis = SUM(shipment_items.quantity) — jamais parsé depuis le texte
          const totalPackages = itemsData?.reduce((sum, item) => sum + (parseInt(item?.quantity, 10) || 0), 0);

          // ✅ OBJECTIF 2 : Détail des colis — chaque ligne = "qty Nom description"
          const packageDetails = itemsData && itemsData?.length > 0
            ? itemsData
                ?.map(item => {
                  const qty = parseInt(item?.quantity, 10) || 1;
                  let desc = '';
                  if (item?.catalog_items) {
                    const catalogName = item?.catalog_items?.name || '';
                    const catalogDesc = item?.catalog_items?.description || '';
                    desc = catalogName + (catalogDesc ? ` – ${catalogDesc}` : '');
                  }
                  if (!desc || desc?.trim() === '') {
                    desc = 'Article';
                  }
                  return `${qty} ${desc?.trim()}`;
                })
                ?.join('\n')
            : '-';

          return {
            ...shipment,
            totalPackages,
            packageDetails
          };
        })
      );

      // Generate CSV data (keep for backwards compatibility)
      const csvRows = [];
      
      // Header row
      csvRows?.push([
        'ContainerCode',
        'ShipmentReference',
        'SenderName',
        'SenderPhone',
        'SenderAddress',
        'SenderCity',
        'SenderCountry',
        'ReceiverName',
        'ReceiverPhone',
        'ReceiverAddress',
        'ReceiverCity',
        'ReceiverCountry',
        'Status'
      ]);

      // Data rows
      shipmentsWithPackages?.forEach(shipment => {
        csvRows?.push([
          container?.containerCode,
          shipment?.reference,
          shipment?.senderName,
          shipment?.senderPhone,
          shipment?.senderAddress,
          shipment?.senderCity,
          shipment?.senderCountry,
          shipment?.receiverName,
          shipment?.receiverPhone,
          shipment?.receiverAddress,
          shipment?.receiverCity,
          shipment?.receiverCountry,
          shipment?.currentStatus
        ]);
      });

      return {
        container,
        csvRows,
        shipments: shipmentsWithPackages
      };
    } catch (error) {
      console.error('Error generating manifest data:', error);
      throw error;
    }
  },

  /**
   * Export container manifest as CSV file
   */
  downloadManifestCSV(csvRows, containerCode) {
    // Convert array to CSV string
    const csvContent = csvRows?.map(row => row?.map(cell => `"${cell || ''}"`)?.join(','))?.join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link?.setAttribute('href', url);
    link?.setAttribute('download', `manifest_${containerCode}.csv`);
    link.style.visibility = 'hidden';
    
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
  },

  /**
   * Export container manifest as clean, readable PDF with logo and detailed package info
   */
  async downloadManifestPDF(container, shipments) {
    try {
      // Create new PDF document
      const doc = new jsPDF();
      
      const pageWidth = doc?.internal?.pageSize?.width;
      let currentY = 20;

      // Try to add company logo if available
      try {
        const logoPath = '/assets/images/banniere_adt-1763665656406.png';
        const logoImg = new Image();
        logoImg.src = logoPath;
        
        await new Promise((resolve) => {
          logoImg.onload = () => {
            const logoWidth = 40;
            const logoHeight = (logoImg.height / logoImg.width) * logoWidth;
            const logoX = (pageWidth - logoWidth) / 2;
            doc?.addImage(logoImg, 'PNG', logoX, currentY, logoWidth, logoHeight);
            currentY += logoHeight + 10;
            resolve();
          };
          logoImg.onerror = () => {
            console.warn('Could not load company logo');
            resolve();
          };
        });
      } catch (logoError) {
        console.warn('Logo loading error:', logoError);
      }

      // Add title
      doc?.setFontSize(16);
      doc?.setFont('helvetica', 'bold');
      const title = `MANIFESTE — ${container?.containerCode}`;
      doc?.text(title, pageWidth / 2, currentY, { align: 'center' });
      currentY += 10;
      
      // Add container info
      doc?.setFontSize(10);
      doc?.setFont('helvetica', 'normal');
      doc?.text(`Date d'édition : ${new Date()?.toLocaleDateString('fr-FR')}`, 14, currentY);
      doc?.text(`Port départ : ${container?.departurePort || '-'}`, 14, currentY + 5);
      doc?.text(`Port destination : ${container?.destinationPort || '-'}`, 14, currentY + 10);
      if (container?.departureDate) {
        doc?.text(`Date de départ : ${new Date(container?.departureDate)?.toLocaleDateString('fr-FR')}`, 14, currentY + 15);
        currentY += 25;
      } else {
        currentY += 20;
      }

      // ✅ OBJECTIF 3 & 5 : Calculs globaux depuis shipment_items.quantity
      const totalEnvois = shipments?.length;
      // ✅ SOURCE DE VÉRITÉ : SUM(shipment_items.quantity) de tous les shipments
      const totalColis = shipments?.reduce((sum, s) => sum + (s?.totalPackages || 0), 0);
      const totalClients = new Set(shipments?.map(s => s?.senderPhone))?.size;

      // ✅ OBJECTIF 2 : Tableau logistique professionnel avec colonnes séparées
      // Colonnes : N° | Référence | Expéditeur | Destinataire | Nb colis | Détail des colis | Paiement
      const tableData = shipments?.map((shipment, index) => {
        const paymentLabels = {
          'paye': 'Payé',
          'partiel': 'Partiel',
          'non_paye': 'Non payé'
        };
        const paymentStatus = paymentLabels?.[shipment?.paymentStatus] || 'Non payé';

        // ✅ Nb colis = SUM(shipment_items.quantity) — colonne numérique indépendante
        const nbColis = shipment?.totalPackages || 0;

        const expediteur = `${shipment?.senderName || '-'}\n${shipment?.senderPhone || ''}`;
        const destinataire = `${shipment?.receiverName || '-'}\n${shipment?.receiverPhone || ''}`;

        return [
          index + 1,
          shipment?.reference || '-',
          expediteur,
          destinataire,
          nbColis,
          shipment?.packageDetails || '-',
          paymentStatus
        ];
      });

      // ✅ OBJECTIF 3 : Ligne TOTAL visuellement distincte à la fin du tableau
      tableData?.push([
        { content: 'TOTAL', styles: { fontStyle: 'bold', fillColor: [220, 220, 220] } },
        { content: `${totalEnvois} ENVOIS`, styles: { fontStyle: 'bold', fillColor: [220, 220, 220] } },
        { content: '', styles: { fillColor: [220, 220, 220] } },
        { content: '', styles: { fillColor: [220, 220, 220] } },
        { content: String(totalColis), styles: { fontStyle: 'bold', fillColor: [220, 220, 220], halign: 'center' } },
        { content: '', styles: { fillColor: [220, 220, 220] } },
        { content: '', styles: { fillColor: [220, 220, 220] } }
      ]);

      autoTable(doc, {
        startY: currentY,
        head: [['N°', 'Référence', 'Expéditeur', 'Destinataire', 'Nb colis', 'Détail des colis', 'Paiement']],
        body: tableData,
        theme: 'grid',
        headStyles: {
          fillColor: [34, 197, 94],
          textColor: 255,
          fontStyle: 'bold',
          fontSize: 9
        },
        bodyStyles: {
          fontSize: 8,
          cellPadding: 3
        },
        columnStyles: {
          0: { cellWidth: 10, halign: 'center' },   // N°
          1: { cellWidth: 30 },                       // Référence
          2: { cellWidth: 35 },                       // Expéditeur
          3: { cellWidth: 35 },                       // Destinataire
          4: { cellWidth: 15, halign: 'center' },     // Nb colis — colonne numérique indépendante
          5: { cellWidth: 45 },                       // Détail des colis
          6: { cellWidth: 20, halign: 'center' }      // Paiement
        },
        styles: {
          overflow: 'linebreak',
          cellPadding: 3,
          fontSize: 8,
          valign: 'middle'
        },
        margin: { left: 7, right: 7 }
      });
      
      // ✅ OBJECTIF 3 : Synthèse sous le tableau
      const finalY = doc?.lastAutoTable?.finalY + 10;
      doc?.setFontSize(10);
      doc?.setFont('helvetica', 'bold');
      doc?.text(`Total clients : ${totalClients}`, 14, finalY);
      doc?.text(`Total envois : ${totalEnvois}`, 14, finalY + 7);
      doc?.text(`Total colis : ${totalColis}`, 14, finalY + 14);
      
      // Add footer
      const footerY = doc?.internal?.pageSize?.height - 15;
      doc?.setFontSize(8);
      doc?.setFont('helvetica', 'italic');
      doc?.setTextColor(128);
      doc?.text('Document généré automatiquement — ADT Express', pageWidth / 2, footerY, { align: 'center' });
      
      doc?.save(`manifeste_${container?.containerCode}.pdf`);
      
      return { success: true };
    } catch (error) {
      console.error('Error generating PDF manifest:', error);
      throw error;
    }
  },

  /**
   * Assign shipment to container
   */
  async assignShipmentToContainer(shipmentId, containerId) {
    try {
      const { data, error } = await supabase
        ?.from('shipments')
        ?.update({ container_id: containerId })
        ?.eq('id', shipmentId)
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        success: true,
        shipmentId: data?.id,
        containerId: data?.container_id
      };
    } catch (error) {
      console.error('Error assigning shipment to container:', error);
      throw error;
    }
  },

  /**
   * Remove shipment from container
   */
  async removeShipmentFromContainer(shipmentId) {
    try {
      const { data, error } = await supabase
        ?.from('shipments')
        ?.update({ container_id: null })
        ?.eq('id', shipmentId)
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        success: true,
        shipmentId: data?.id
      };
    } catch (error) {
      console.error('Error removing shipment from container:', error);
      throw error;
    }
  },

  /**
   * Calculate total revenue for a container
   */
  async getContainerRevenue(containerId) {
    try {
      const { data: shipmentsData, error } = await supabase
        ?.from('shipments')
        ?.select('paid_amount, discount_amount')
        ?.eq('container_id', containerId);

      if (error) throw error;

      // Calculate total revenue (sum of paid amounts)
      const totalRevenue = shipmentsData?.reduce((sum, shipment) => {
        return sum + parseFloat(shipment?.paid_amount || 0);
      }, 0);

      // Calculate total value (would include unpaid amounts too)
      const { data: itemsData } = await supabase
        ?.from('shipment_items')
        ?.select('price_total, shipment_id')
        ?.in('shipment_id', shipmentsData?.map(s => s?.id));

      // Group items by shipment to calculate total value
      const shipmentTotals = new Map();
      itemsData?.forEach(item => {
        const current = shipmentTotals?.get(item?.shipment_id) || 0;
        shipmentTotals?.set(item?.shipment_id, current + parseFloat(item?.price_total || 0));
      });

      const totalValue = Array.from(shipmentTotals?.values())?.reduce((sum, val) => sum + val, 0);
      const totalDiscount = shipmentsData?.reduce((sum, shipment) => sum + parseFloat(shipment?.discount_amount || 0), 0);

      return {
        totalRevenue: totalRevenue,
        totalValue: totalValue,
        totalDiscount: totalDiscount,
        outstandingAmount: totalValue - totalDiscount - totalRevenue
      };
    } catch (error) {
      console.error('Error calculating container revenue:', error);
      throw error;
    }
  },

  /**
   * Upload document to container
   */
  async uploadContainerDocument(containerId, file, documentType = 'general') {
    try {
      const timestamp = Date.now();
      const sanitizedFileName = file?.name?.replace(/[^a-zA-Z0-9.-]/g, '_');
      const filePath = `${containerId}/${documentType}_${timestamp}_${sanitizedFileName}`;

      const { data, error } = await supabase?.storage
        ?.from('container-documents')
        ?.upload(filePath, file);

      if (error) throw error;

      return {
        success: true,
        filePath: data?.path,
        fileName: file?.name,
        fileSize: file?.size,
        documentType: documentType
      };
    } catch (error) {
      console.error('Error uploading container document:', error);
      throw error;
    }
  },

  /**
   * Get all documents for a container
   */
  async getContainerDocuments(containerId) {
    try {
      const { data: fileList, error } = await supabase?.storage
        ?.from('container-documents')
        ?.list(containerId, { sortBy: { column: 'created_at', order: 'desc' } });

      if (error) throw error;

      // Generate signed URLs for each document
      const documentsWithUrls = await Promise.all(
        fileList?.map(async (file) => {
          const filePath = `${containerId}/${file?.name}`;
          
          const { data: urlData, error: urlError } = await supabase?.storage
            ?.from('container-documents')
            ?.createSignedUrl(filePath, 3600); // 1 hour expiry

          if (urlError) {
            console.error('Error creating signed URL:', urlError);
            return null;
          }

          return {
            name: file?.name,
            filePath: filePath,
            fileUrl: urlData?.signedUrl,
            size: file?.metadata?.size,
            createdAt: file?.created_at,
            updatedAt: file?.updated_at
          };
        })
      );

      return documentsWithUrls?.filter(doc => doc !== null);
    } catch (error) {
      console.error('Error fetching container documents:', error);
      throw error;
    }
  },

  /**
   * Delete document from container
   */
  async deleteContainerDocument(filePath) {
    try {
      const { error } = await supabase?.storage
        ?.from('container-documents')
        ?.remove([filePath]);

      if (error) throw error;

      return { success: true };
    } catch (error) {
      console.error('Error deleting container document:', error);
      throw error;
    }
  },

  /**
   * Download container document
   */
  async downloadContainerDocument(filePath, fileName) {
    try {
      const { data, error } = await supabase?.storage
        ?.from('container-documents')
        ?.download(filePath);

      if (error) throw error;

      // Create blob and trigger download
      const url = URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || filePath?.split('/')?.pop();
      link.style.visibility = 'hidden';
      
      document.body?.appendChild(link);
      link?.click();
      document.body?.removeChild(link);
      URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      console.error('Error downloading container document:', error);
      throw error;
    }
  },

  /**
   * Send webhook notification to N8N when container status is updated
   */
  async sendN8nWebhook(containerData, clientPhone) {
    // ✅ FIX: Declare variables outside try/catch so they are accessible in catch block
    const webhookUrl = import.meta.env?.VITE_N8N_WEBHOOK_URL;
    const webhookSecret = import.meta.env?.VITE_N8N_WEBHOOK_SECRET || 'default-secret-change-me';
    const payload = {
      status: containerData?.status,
      reference: containerData?.containerCode,
      to: clientPhone || null,
      container_id: containerData?.id
    };

    try {
      // Skip webhook if URL not configured
      if (!webhookUrl || webhookUrl === 'your-n8n-webhook-url-here') {
        console.warn('[N8N Webhook] URL not configured, skipping webhook call');
        return { success: true, skipped: true };
      }

      console.log('[N8N Webhook] Sending to:', webhookUrl, '| Payload:', JSON.stringify(payload));

      // Send POST request with no-cors to bypass CORS restrictions from browser
      await fetch(webhookUrl, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json',
          'x-adt-secret': webhookSecret
        },
        body: JSON.stringify(payload)
      });

      // With no-cors mode, response is opaque - we can't check status but request was sent
      console.log('[N8N Webhook] ✅ Request sent (opaque response - no-cors mode)');
      return { success: true };
    } catch (error) {
      // Log error to n8n_webhook_logs table for tracking and retry
      const errorLog = {
        container_id: payload?.container_id || null,
        webhook_url: webhookUrl,
        payload: payload,
        status: 'FAILED',
        http_status_code: error?.status || null,
        error_message: error?.message || 'Unknown webhook error',
        retry_count: 0,
        created_at: new Date()?.toISOString()
      };

      try {
        await supabase?.from('n8n_webhook_logs')?.insert(errorLog);
        console.error('[N8N Webhook] Error logged to database:', error?.message);
      } catch (logError) {
        console.error('[N8N Webhook] Failed to log webhook error to database:', logError);
      }

      console.error('[N8N Webhook] ❌ Error sending webhook (non-blocking):', error);
      return { success: false, error: error?.message };
    }
  },

  /**
   * Send N8N webhooks to all clients in container
   */
  async sendN8nWebhookToAllClients(containerId, containerData) {
    try {
      // ✅ CRITICAL: Always send at least one webhook for the container status change
      // even if no client phones are found
      const webhookUrl = import.meta.env?.VITE_N8N_WEBHOOK_URL;
      if (!webhookUrl || webhookUrl === 'your-n8n-webhook-url-here') {
        console.warn('[N8N Webhook] URL not configured, skipping batch webhook');
        return { success: true, sent: 0, skipped: true };
      }

      // ✅ CRITICAL: Add container_id to containerData for webhook payload
      const enrichedContainerData = {
        ...containerData,
        id: containerId
      };

      // Get all shipments with client phone numbers
      const shipments = await this.getContainerShipments(containerId);
      const clientPhones = [...new Set((shipments || []).map(s => s?.senderPhone).filter(Boolean))];

      console.log(`[N8N Webhook] Container ${containerData?.containerCode} | Status: ${containerData?.status} | Clients with phones: ${clientPhones?.length}`);

      if (clientPhones?.length === 0) {
        // ✅ FIX: Still send a single container-level webhook even with no client phones
        console.log('[N8N Webhook] No client phones found — sending container-level webhook without phone');
        const result = await this.sendN8nWebhook(enrichedContainerData, null);
        return { success: result?.success, sent: result?.success ? 1 : 0, failed: result?.success ? 0 : 1, total: 1 };
      }

      // Send webhook to each client
      const webhookPromises = clientPhones?.map(phone =>
        this.sendN8nWebhook(enrichedContainerData, phone)
      );

      const results = await Promise.allSettled(webhookPromises);

      const successCount = results?.filter(r => r?.status === 'fulfilled' && r?.value?.success)?.length;
      const failureCount = results?.filter(r => r?.status === 'rejected' || !r?.value?.success)?.length;

      console.log(`[N8N Webhook] Results: ${successCount} sent, ${failureCount} failed out of ${clientPhones?.length} clients`);

      // Log summary to database if there were failures
      if (failureCount > 0) {
        try {
          await supabase?.from('n8n_webhook_logs')?.insert({
            container_id: containerId,
            webhook_url: import.meta.env?.VITE_N8N_WEBHOOK_URL,
            payload: { summary: 'Batch webhook send', containerData },
            status: 'PARTIAL_FAILURE',
            error_message: `${failureCount} out of ${clientPhones?.length} webhooks failed`,
            retry_count: 0,
            created_at: new Date()?.toISOString()
          });
        } catch (logError) {
          console.error('[N8N Webhook] Failed to log batch webhook summary:', logError);
        }
      }

      return {
        success: true,
        sent: successCount,
        failed: failureCount,
        total: clientPhones?.length
      };
    } catch (error) {
      // Log critical error to database
      try {
        await supabase?.from('n8n_webhook_logs')?.insert({
          container_id: containerId,
          webhook_url: import.meta.env?.VITE_N8N_WEBHOOK_URL,
          payload: { containerData },
          status: 'FAILED',
          error_message: `Critical error in batch webhook send: ${error?.message}`,
          retry_count: 0,
          created_at: new Date()?.toISOString()
        });
      } catch (logError) {
        console.error('[N8N Webhook] Failed to log critical webhook error:', logError);
      }

      console.error('[N8N Webhook] ❌ Critical error in batch webhook send:', error);
      return { success: false, error: error?.message };
    }
  },

  /**
   * Archive a container (soft delete)
   */
  async archive(containerId) {
    try {
      const { data, error } = await supabase
        ?.from('containers')
        ?.update({ is_archived: true, updated_at: new Date()?.toISOString() })
        ?.eq('id', containerId)
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error archiving container:', error);
      throw error;
    }
  },

  /**
   * Unarchive a container (restore)
   */
  async unarchive(containerId) {
    try {
      const { data, error } = await supabase
        ?.from('containers')
        ?.update({ is_archived: false, updated_at: new Date()?.toISOString() })
        ?.eq('id', containerId)
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error unarchiving container:', error);
      throw error;
    }
  }
};

export default containerService;

// Export constants for use in components
export { CONTAINER_STATUS_CODES, STATUS_DISPLAY_LABELS, LABEL_TO_CODE };
function getContainerById(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: getContainerById is not implemented yet.', args);
  return null;
}

export { getContainerById };
function getContainers(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: getContainers is not implemented yet.', args);
  return null;
}

export { getContainers };
function updateContainerStatus(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: updateContainerStatus is not implemented yet.', args);
  return null;
}

export { updateContainerStatus };
function getStatusColor(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: getStatusColor is not implemented yet.', args);
  return null;
}

export { getStatusColor };